import React from "react";
import styled from "styled-components";

import { MAX_FIT_SCALE } from "../../constants";

// Renders `children` inside a virtual `width × height` coordinate system,
// scaled with `transform: scale(S)` to fit the largest possible aspect-
// preserving rectangle inside the outer box (which sizes itself to 100% of
// its parent). The scaled content is centered within the outer box via a
// translate in the same transform.
//
// Why this vs. CSS `aspect-ratio` / `object-fit`: CSS can't reliably
// render "this fixed-coord-system at the biggest aspect-preserving size
// that fits" for arbitrary DOM inside flex/grid. `transform: scale` is
// bulletproof and decouples the children's layout math (in virtual coords)
// from the pixel size they happen to display at.
// `align` controls horizontal alignment of the visible (scaled) frame
// inside the outer box — "center" (default), "start", or "end". Matters
// when the outer is wider than the scaled frame (e.g. viewport wide but
// short, or a max-scale cap making the frame smaller than its Pane cell).
// No global upscale cap — MAX_FIT_SCALE is Infinity by default, so the
// frame grows until the parent container caps it. Callers can still pass
// a stricter `maxScale` to force a smaller render.
function FitContainer({
  width,
  height,
  children,
  className,
  align = "center",
  maxScale,
}) {
  const outerRef = React.useRef(null);
  const [fit, setFit] = React.useState(null);

  React.useLayoutEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;
    const compute = () => {
      const rect = outer.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const cap = maxScale ?? MAX_FIT_SCALE;
      const s = Math.min(rect.width / width, rect.height / height, cap);
      const spareX = rect.width - width * s;
      const ox = align === "start" ? 0 : align === "end" ? spareX : spareX / 2;
      setFit({
        scale: s,
        offsetX: ox,
        offsetY: (rect.height - height * s) / 2,
      });
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(outer);
    return () => ro.disconnect();
  }, [width, height, align, maxScale]);

  // Inner mounts immediately (refs / canvases / image preloads stay
  // wired up) but hides until the first measurement lands — otherwise
  // it would briefly paint at scale 1, i.e. children at full virtual
  // size, before useLayoutEffect's setFit collapses them to fit.
  return (
    <Outer ref={outerRef} className={className}>
      <Inner
        style={{
          width,
          height,
          transform: fit
            ? `translate(${fit.offsetX}px, ${fit.offsetY}px) scale(${fit.scale})`
            : undefined,
          visibility: fit ? "visible" : "hidden",
        }}
      >
        {children}
      </Inner>
    </Outer>
  );
}

const Outer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  /* overflow: hidden; */
`;

const Inner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: 0 0;
`;

export default FitContainer;
